import * as THREE from "three";
import {getSegmentUVLength} from "./BasicPathUtils_UVs.js";
import {Vec2i} from "./SharedPathUtils.js";

// Data classes for storing segment information
enum SegmentType {
    Straight = "Straight",
    Joint = "Joint"
}

class PathVertex {
    constructor(
        public position: Vec2i,
        public uv: Vec2i
    ) {}
}

export class PathSegment {
    constructor(
        public type: SegmentType,
        public leftVertices: PathVertex[],
        public rightVertices: PathVertex[]
    ) {}
}

// Main functions for road generation
export function CreateGeometryForPath(
	pathPoints: Vec2i[],
	roadWidth: number = 2,
	uvCoversXPathWidths_alongPathWidth: number = 1,
	uvCoversXMeters_alongPathLength: number = 1,
	maxTurnPerArcSegmentDeg?: number|n,
): THREE.BufferGeometry|n {
	if (pathPoints.length < 2) {
		return null; // need 2+ points to make a displayable geometry
	}

	const segments = generatePathSegments(pathPoints, roadWidth, uvCoversXPathWidths_alongPathWidth, uvCoversXMeters_alongPathLength, maxTurnPerArcSegmentDeg);
	//console.log("Generated segments:", segments);
	return convertSegmentsToGeometry(segments);
}

function generatePathSegments(
	pathPoints: Vec2i[],
	roadWidth: number,
	uvCoversXPathWidths_alongPathWidth: number,
	uvCoversXMeters_alongPathLength: number,
	maxTurnPerArcSegmentDeg?: number|n,
): PathSegment[] {
	if (pathPoints.length < 2) {
		throw new Error("Need at least 2 path points");
	}

	const segments: PathSegment[] = [];
	const halfWidth = roadWidth / 2;

	// Calculate truncation points for all segments
	const truncationPoints = calculateTruncationPoints(pathPoints, halfWidth);

	// Handle first segment (always straight)
	const firstStraight = createStraightSegment(
		truncationPoints[0].start,
		truncationPoints[0].end,
		halfWidth,
		0,
		uvCoversXPathWidths_alongPathWidth,
		uvCoversXMeters_alongPathLength
	);
	segments.push(firstStraight);

	let currentUV = getSegmentUVLength(firstStraight);

	// Handle middle segments (joint + straight)
	for (let i = 1; i < pathPoints.length - 1; i++) {
		const prevPoint = pathPoints[i - 1];
		const currentPoint = pathPoints[i];
		const nextPoint = pathPoints[i + 1];

		// Create joint segment
		const joint = createJointSegment(
			prevPoint,
			currentPoint,
			nextPoint,
			halfWidth,
			currentUV,
			uvCoversXPathWidths_alongPathWidth,
			uvCoversXMeters_alongPathLength,
			maxTurnPerArcSegmentDeg,
		);
		segments.push(joint);
		currentUV += getSegmentUVLength(joint);

		// Create straight segment to next point
		const straight = createStraightSegment(
			truncationPoints[i].start,
			truncationPoints[i].end,
			halfWidth,
			currentUV,
			uvCoversXPathWidths_alongPathWidth,
			uvCoversXMeters_alongPathLength
		);
		segments.push(straight);
		currentUV += getSegmentUVLength(straight);
	}

	return segments;
}

function calculateTruncationPoints(pathPoints: Vec2i[], halfWidth: number): {start: Vec2i, end: Vec2i}[] {
	const truncationPoints: {start: Vec2i, end: Vec2i}[] = [];

	for (let i = 0; i < pathPoints.length - 1; i++) {
		const currentPoint = pathPoints[i];
		const nextPoint = pathPoints[i + 1];

		let startPoint = currentPoint;
		let endPoint = nextPoint;

		// For segments that aren't the first, calculate where they should start
		// (after the previous joint)
		if (i > 0) {
			const prevPoint = pathPoints[i - 1];
			const truncationDistance = calculateTruncationDistance(prevPoint, currentPoint, nextPoint, halfWidth);
			const direction = Vec2i.subtract(nextPoint, currentPoint).normalize();
			startPoint = Vec2i.add(currentPoint, Vec2i.scale(direction, truncationDistance));
		}

		// For segments that aren't the last, calculate where they should end
		// (before the next joint)
		if (i < pathPoints.length - 2) {
			const nextNextPoint = pathPoints[i + 2];
			const truncationDistance = calculateTruncationDistance(currentPoint, nextPoint, nextNextPoint, halfWidth);
			const direction = Vec2i.subtract(currentPoint, nextPoint).normalize();
			endPoint = Vec2i.add(nextPoint, Vec2i.scale(direction, truncationDistance));
		}

		truncationPoints.push({start: startPoint, end: endPoint});
	}

	return truncationPoints;
}

function calculateTruncationDistance(prevPoint: Vec2i, currentPoint: Vec2i, nextPoint: Vec2i, halfWidth: number): number {
	const incomingDir = Vec2i.subtract(currentPoint, prevPoint).normalize();
	const outgoingDir = Vec2i.subtract(nextPoint, currentPoint).normalize();
	const incomingPerp = incomingDir.perpendicular();
	const outgoingPerp = outgoingDir.perpendicular();

	// Determine if this is a left or right turn
	const cross = incomingDir.x * outgoingDir.y - incomingDir.y * outgoingDir.x;
	const isLeftTurn = cross > 0;

	// Calculate the inner edge lines (these will intersect at the pivot point)
	let innerEdge1Start: Vec2i, innerEdge1Dir: Vec2i;
	let innerEdge2Start: Vec2i, innerEdge2Dir: Vec2i;

	if (isLeftTurn) {
		// Left turn: left edges are inner
		innerEdge1Start = Vec2i.add(currentPoint, Vec2i.scale(incomingPerp, halfWidth));
		innerEdge1Dir = incomingDir;
		innerEdge2Start = Vec2i.add(currentPoint, Vec2i.scale(outgoingPerp, halfWidth));
		innerEdge2Dir = Vec2i.scale(outgoingDir, -1);
	} else {
		// Right turn: right edges are inner
		innerEdge1Start = Vec2i.add(currentPoint, Vec2i.scale(incomingPerp, -halfWidth));
		innerEdge1Dir = incomingDir;
		innerEdge2Start = Vec2i.add(currentPoint, Vec2i.scale(outgoingPerp, -halfWidth));
		innerEdge2Dir = Vec2i.scale(outgoingDir, -1);
	}

	// Find the intersection point (pivot point)
	const pivotPoint = findLineIntersection(
		innerEdge1Start,
		Vec2i.add(innerEdge1Start, innerEdge1Dir),
		innerEdge2Start,
		Vec2i.add(innerEdge2Start, innerEdge2Dir)
	);

	// Calculate the distance from the current point to the pivot point along the path direction
	const toPivot = Vec2i.subtract(pivotPoint, currentPoint);
	const distanceAlongIncoming = Vec2i.dot(toPivot, incomingDir);
	const distanceAlongOutgoing = Vec2i.dot(toPivot, outgoingDir);

	// Return the appropriate distance based on which direction we're measuring
	// For incoming segments, we want the distance along the incoming direction
	// For outgoing segments, we want the distance along the outgoing direction
	// Since this function is used for both, we'll return the average or use a heuristic
	return Math.abs(distanceAlongIncoming);
}

function createStraightSegment(
	startPoint: Vec2i,
	endPoint: Vec2i,
	halfWidth: number,
	startUV: number,
	uvCoversXPathWidths_alongPathWidth: number,
	uvCoversXMeters_alongPathLength: number
): PathSegment {
	const direction = Vec2i.subtract(endPoint, startPoint);
	const length = direction.length();
	const normalizedDir = direction.normalize();
	const perpendicular = normalizedDir.perpendicular();

	const leftOffset = Vec2i.scale(perpendicular, halfWidth);
	const rightOffset = Vec2i.scale(perpendicular, -halfWidth);

	const leftStart = Vec2i.add(startPoint, leftOffset);
	const leftEnd = Vec2i.add(endPoint, leftOffset);
	const rightStart = Vec2i.add(startPoint, rightOffset);
	const rightEnd = Vec2i.add(endPoint, rightOffset);

	const uvLength = length / uvCoversXMeters_alongPathLength;
	const endUV = startUV + uvLength;

	const leftVertices = [
		new PathVertex(leftStart, new Vec2i(0, startUV)),
		new PathVertex(leftEnd, new Vec2i(0, endUV))
	];

	const rightVertices = [
		new PathVertex(rightStart, new Vec2i(uvCoversXPathWidths_alongPathWidth, startUV)),
		new PathVertex(rightEnd, new Vec2i(uvCoversXPathWidths_alongPathWidth, endUV))
	];

	return new PathSegment(SegmentType.Straight, leftVertices, rightVertices);
}

function createJointSegment(
	prevPoint: Vec2i,
	currentPoint: Vec2i,
	nextPoint: Vec2i,
	halfWidth: number,
	startUV: number,
	uvCoversXPathWidths_alongPathWidth: number,
	uvCoversXMeters_alongPathLength: number,
	maxTurnPerArcSegmentDeg?: number|n,
): PathSegment {
	const incomingDir = Vec2i.subtract(currentPoint, prevPoint).normalize();
	const outgoingDir = Vec2i.subtract(nextPoint, currentPoint).normalize();

	// Calculate truncation distances
	const incomingTruncation = calculateTruncationDistance(prevPoint, currentPoint, nextPoint, halfWidth);
	const outgoingTruncation = calculateTruncationDistance(prevPoint, currentPoint, nextPoint, halfWidth);

	// Find the actual start and end points of the joint (where straight segments end/begin)
	const jointStart = Vec2i.add(currentPoint, Vec2i.scale(incomingDir, -incomingTruncation));
	const jointEnd = Vec2i.add(currentPoint, Vec2i.scale(outgoingDir, outgoingTruncation));

	const incomingPerp = incomingDir.perpendicular();
	const outgoingPerp = outgoingDir.perpendicular();

	// Get the actual edge points where the straight segments end/start
	const prevLeftEnd = Vec2i.add(jointStart, Vec2i.scale(incomingPerp, halfWidth));
	const prevRightEnd = Vec2i.add(jointStart, Vec2i.scale(incomingPerp, -halfWidth));
	const nextLeftStart = Vec2i.add(jointEnd, Vec2i.scale(outgoingPerp, halfWidth));
	const nextRightStart = Vec2i.add(jointEnd, Vec2i.scale(outgoingPerp, -halfWidth));

	// Determine if this is a left or right turn
	const cross = incomingDir.x * outgoingDir.y - incomingDir.y * outgoingDir.x;
	const isLeftTurn = cross > 0;

	// Find the intersection point of the inner edges (this will be our pivot point)
	let pivotPoint: Vec2i;
	let arcStartPoint: Vec2i;
	let arcEndPoint: Vec2i;

	if (isLeftTurn) {
		// Left turn: left side is the pivot, right side arcs
		pivotPoint = findLineIntersection(prevLeftEnd, Vec2i.add(prevLeftEnd, incomingDir),
										 nextLeftStart, Vec2i.add(nextLeftStart, Vec2i.scale(outgoingDir, -1)));
		arcStartPoint = prevRightEnd;
		arcEndPoint = nextRightStart;
	} else {
		// Right turn: right side is the pivot, left side arcs
		pivotPoint = findLineIntersection(prevRightEnd, Vec2i.add(prevRightEnd, incomingDir),
										 nextRightStart, Vec2i.add(nextRightStart, Vec2i.scale(outgoingDir, -1)));
		arcStartPoint = prevLeftEnd;
		arcEndPoint = nextLeftStart;
	}

	// Calculate the arc
	const startAngle = Math.atan2(arcStartPoint.y - pivotPoint.y, arcStartPoint.x - pivotPoint.x);
	const endAngle = Math.atan2(arcEndPoint.y - pivotPoint.y, arcEndPoint.x - pivotPoint.x);
	const radius = Vec2i.subtract(arcStartPoint, pivotPoint).length();

	// Calculate the shortest arc direction
	let angleDiff = endAngle - startAngle;
	if (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
	if (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

	// Number of arc segments: when `maxTurnPerArcSegmentDeg` is set, scale by turn angle so
	// each arc segment sweeps at most that many degrees. When null/undefined (legacy paths),
	// fall back to the historical hard-coded 8 segments. Clamp to [1, 32] for safety against
	// pathological inputs (0° → 1 segment min; tiny maxTurn values → 32 cap).
	const angleDeg = Math.abs(angleDiff) * 180 / Math.PI;
	const segments = maxTurnPerArcSegmentDeg != null
		? Math.min(32, Math.max(1, Math.ceil(angleDeg / maxTurnPerArcSegmentDeg)))
		: 8;
	const arcLength = Math.abs(angleDiff) * radius;
	const uvLength = arcLength / uvCoversXMeters_alongPathLength;

	const leftVertices: PathVertex[] = [];
	const rightVertices: PathVertex[] = [];

	for (let i = 0; i <= segments; i++) {
		const t = i / segments;
		const currentAngle = startAngle + angleDiff * t;
		const arcPoint = Vec2i.add(pivotPoint, new Vec2i(Math.cos(currentAngle) * radius, Math.sin(currentAngle) * radius));

		const uvV = startUV + uvLength * t;

		if (isLeftTurn) {
			// Left turn: left side is pivot, right side arcs
			leftVertices.push(new PathVertex(pivotPoint, new Vec2i(0, uvV)));
			rightVertices.push(new PathVertex(arcPoint, new Vec2i(uvCoversXPathWidths_alongPathWidth, uvV)));
		} else {
			// Right turn: left side arcs, right side is pivot
			leftVertices.push(new PathVertex(arcPoint, new Vec2i(0, uvV)));
			rightVertices.push(new PathVertex(pivotPoint, new Vec2i(uvCoversXPathWidths_alongPathWidth, uvV)));
		}
	}

	return new PathSegment(SegmentType.Joint, leftVertices, rightVertices);
}

function findLineIntersection(p1: Vec2i, p2: Vec2i, p3: Vec2i, p4: Vec2i): Vec2i {
	const x1 = p1.x, y1 = p1.y;
	const x2 = p2.x, y2 = p2.y;
	const x3 = p3.x, y3 = p3.y;
	const x4 = p4.x, y4 = p4.y;

	const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
	if (Math.abs(denom) < 1e-10) {
		// Lines are parallel, return midpoint as fallback
		return new Vec2i((p1.x + p3.x) / 2, (p1.y + p3.y) / 2);
	}

	const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
	return new Vec2i(x1 + t * (x2 - x1), y1 + t * (y2 - y1));
}

function getSegmentLength(segment: PathSegment): number {
	if (segment.leftVertices.length < 2) return 0;

	let length = 0;
	for (let i = 1; i < segment.leftVertices.length; i++) {
		const prev = segment.leftVertices[i - 1].position;
		const curr = segment.leftVertices[i].position;
		length += Vec2i.subtract(curr, prev).length();
	}
	return length;
}

function convertSegmentsToGeometry(segments: PathSegment[]): THREE.BufferGeometry {
	const vertices: number[] = [];
	const uvs: number[] = [];
	const indices: number[] = [];

	let vertexIndex = 0;

	for (const segment of segments) {
		const leftVerts = segment.leftVertices;
		const rightVerts = segment.rightVertices;

		if (leftVerts.length !== rightVerts.length) {
			throw new Error("Left and right vertices must have the same count");
		}

		// Add vertices and UVs
		const segmentStartIndex = vertexIndex;
		for (let i = 0; i < leftVerts.length; i++) {
			// Left vertex
			vertices.push(leftVerts[i].position.x, leftVerts[i].position.y, 0);
			uvs.push(leftVerts[i].uv.x, leftVerts[i].uv.y);

			// Right vertex
			vertices.push(rightVerts[i].position.x, rightVerts[i].position.y, 0);
			uvs.push(rightVerts[i].uv.x, rightVerts[i].uv.y);

			vertexIndex += 2;
		}

		// Add indices for triangles
		for (let i = 0; i < leftVerts.length - 1; i++) {
			const baseIndex = segmentStartIndex + i * 2;

			// First triangle (left-bottom, right-bottom, left-top)
			indices.push(baseIndex, baseIndex + 1, baseIndex + 2);

			// Second triangle (right-bottom, right-top, left-top)
			indices.push(baseIndex + 1, baseIndex + 3, baseIndex + 2);
		}
	}

	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
	geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
	geometry.setIndex(indices);

	return geometry;
}